import type { ReactNode } from 'react';
import type { Cell, CellStatus } from '@balance/domain';
import { eur } from '../format.js';
import { useApp } from '../state.js';

export function Money({ cents, sign, className = '' }: { cents: number | null | undefined; sign?: boolean; className?: string }) {
  const cls = cents === null || cents === undefined ? 'number' : `number ${className}`;
  return <span className={cls}>{eur(cents, { sign })}</span>;
}

/** Nombre de concepto: abre la ficha de reglas de solo lectura desde cualquier vista (C03). */
export function ConceptName({ id, name, className = '' }: { id: string; name: string; className?: string }) {
  const { openConcept } = useApp();
  return (
    <button type="button" className={`linkbutton ${className}`} onClick={() => openConcept(id)} title="Ver reglas del concepto">
      {name}
    </button>
  );
}

export function Badge({ children, kind = '' }: { children: ReactNode; kind?: '' | 'warn' | 'red' | 'neutral' | 'coral' }) {
  return <span className={`badge ${kind}`}>{children}</span>;
}

export const STATUS_LABEL: Record<CellStatus, string> = {
  inactive: 'Sin vigencia',
  none: 'Sin vencimiento',
  expected: 'Previsto',
  partial: 'Parcialmente real',
  actual: 'Real',
  over: 'Exceso',
};

export function StatusDot({ status }: { status: CellStatus }) {
  if (status === 'none' || status === 'inactive') return null;
  return <span className={`dot ${status}`} aria-hidden="true" />;
}

export function cellClass(cell: Cell | undefined): string {
  if (!cell) return '';
  if (cell.status === 'over') return 'over';
  if (cell.status === 'none') return 'none';
  if (cell.status === 'inactive') return 'inactive';
  return '';
}

export function Meter({ name, spent, budget, over, remaining, conceptId, animateFrom }: { name: string; spent: number; budget: number; over: number; remaining: number; conceptId: string; animateFrom?: number }) {
  const isOver = over > 0;
  const width = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  return (
    <div style={{ marginBottom: 25 }}>
      <div className="panelhead" style={{ marginBottom: 6 }}>
        <h3>
          <ConceptName id={conceptId} name={name} />
        </h3>
        <Badge kind={isOver ? 'red' : ''}>{isOver ? 'Exceso' : 'Disponible'}</Badge>
      </div>
      <div className="inline">
        <span className="big" data-count={spent} data-from={animateFrom ?? spent}>
          {eur(spent)}
        </span>
        <span className="muted">de {eur(budget)}</span>
      </div>
      <div className="bartrack" role="progressbar" aria-valuenow={spent} aria-valuemin={0} aria-valuemax={budget} aria-label={`${name}: ${eur(spent)} de ${eur(budget)}`}>
        <div className={`bar${isOver ? ' over' : ''}`} data-width={width} style={{ width: `${width}%` }} />
      </div>
      <div className={`muted${isOver ? ' negative' : ''}`}>{isOver ? `Te pasaste ${eur(over)}` : `Quedan ${eur(remaining)}`}</div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function PendingHint({ children }: { children: ReactNode }) {
  return <div className="hint pending">{children}</div>;
}
